import {
  RedemptionChannelModel,
  RedemptionChannelRequestModel,
} from "libs/models/branches";
import { ClientModel, ClientRequestModel } from "libs/models/client";
import {
  ForgetPasswordChange,
  ForgetPasswordRequestOtp,
  ForgetPasswordVerifyOtp,
} from "libs/models/forget-password";
import { GovernorateModel, LookupModel } from "libs/models/lookup-model";
import { PaginatedList } from "libs/models/paginated-list";
import {
  ProductModel,
  ProductRequestModel,
  UpdateProductRequestModel,
} from "libs/models/products";
import { TransactionsData } from "libs/models/transaction";
import {
  FILE_MANAGMENT_END_POINTS,
  GIFT_CARD_ENDPOINTS,
  USER_MANAGMENT_END_POINTS,
} from "../libs/endpoints";
import { ChangePasswordModel } from "../libs/models/change-password";
import { Response } from "../libs/models/generic-response";
import { LoginModel, LoginResponse } from "../libs/models/login";
import { SignupModel } from "../libs/models/signup";
import { HttpCrossPay, HttpExternalService, HttpFileManagment } from "./https";
import {
  PointOfSaleModel,
  RequestPointOfSaleModel,
  UpdatePointOfSaleModel,
} from "libs/models/point-of-sale-model";
import { UserRequestModel } from "libs/models/user-model";
import {
  InvoiceModel,
  InvoicePaymentRequest,
  InvoiceProductModel,
} from "libs/models/invoice";
import { InvoiceReportResponse } from "libs/models/invoicing-report";
import { RedemptionReportResponse } from "libs/models/redemption-report";
import { ProductInventoryReportResponse } from "libs/models/product-inventory-report";
import { CROSSPAY_ROUTES } from "libs/routes";
import { PowerBiReportModel } from "libs/models/power-bi";
import { DashboardStatisticsModel } from "libs/models/dashboard-statistics";
import {
  BatchModel,
  BatchRequestModel,
  EditBatchRequestModel,
  ProductItemModel,
} from "libs/models/batch-model";

const loginService = async (
  model: LoginModel,
): Promise<Response<LoginResponse> | null> => {
  return await HttpCrossPay.post(USER_MANAGMENT_END_POINTS.LOGIN_ADMIN, model);
};

const refreshToken = async (
  refreshToken: string,
): Promise<Response<LoginResponse> | null> => {
  return await HttpCrossPay.post(USER_MANAGMENT_END_POINTS.REFRESH_TOKEN, {
    refreshToken: refreshToken,
  });
};

const changePassword = async (
  passwordModel: ChangePasswordModel,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.post(
    USER_MANAGMENT_END_POINTS.Change_Password,
    passwordModel,
  );
};

const signupService = async (
  model: SignupModel,
): Promise<Response<LoginResponse> | null> => {
  return await HttpCrossPay.post(
    USER_MANAGMENT_END_POINTS.VENDOR_ADMIN_REGISTRATION,
    model,
  );
};

const requestVerificationService = async (
  email: string,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.post(
    USER_MANAGMENT_END_POINTS.VENDOR_RESEND_VERIFICATION,
    email,
  );
};

const requestForgetPasswordOtpService = async (
  model: ForgetPasswordRequestOtp,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.post(
    USER_MANAGMENT_END_POINTS.FORGET_PASSWORD_REQUEST_OTP,
    model,
  );
};
const verifyForgetPasswordOtpService = async (
  model: ForgetPasswordVerifyOtp,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.post(
    USER_MANAGMENT_END_POINTS.FORGET_PASSWORD_VERIFY_OTP,
    model,
  );
};
const forgetPasswordChangePasswordService = async (
  model: ForgetPasswordChange,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.post(
    USER_MANAGMENT_END_POINTS.FORGET_PASSWORD_CHANGE_PASSWORD,
    model,
  );
};
const transactionReportService = async (
  queryOptions: string | null,
): Promise<Response<PaginatedList<TransactionsData>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.TRANSACTION_REPORT}${queryOptions ?? ""}`,
  );
};
const invoicingReportService = async (
  queryOptions: string | null,
): Promise<Response<InvoiceReportResponse> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.INVOICING_REPORT}${queryOptions ?? ""}`,
  );
};
const getClientService = async (
  queryOptions?: string,
): Promise<Response<PaginatedList<ClientModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GET_CLIENTS}${queryOptions ?? ""}`,
  );
};
const addClientService = async (
  clientModel: ClientRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.ADD_CLIENTS}`,
    clientModel,
  );
};

const editClientService = async (
  clientModel: ClientModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.UPDATE_CLIENT}`,
    clientModel,
  );
};
const getClientDetailsById = async (
  id: string,
): Promise<Response<ClientModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.GET_CLIENTS}/${id}`);
};
const deactivateClientService = async (
  id: string,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.GET_CLIENTS}/${id}/deactivate`,
  );
};
const activateClientService = async (
  id: string,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.GET_CLIENTS}/${id}/activate`,
  );
};
const getRedemptionChannelByIdService = async (
  id: string,
): Promise<Response<RedemptionChannelRequestModel> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.REDEMPTION_CHANNELS}/${id}`,
  );
};
const getFileIdService = async (
  file: File,
): Promise<Response<string> | null> => {
  const formData = new FormData();
  formData.append("file", file);
  return await HttpCrossPay.post(
    FILE_MANAGMENT_END_POINTS.UPLOAD_FILE,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};
const downloadFileByIdService = async (id: string): Promise<File | null> => {
  return await HttpCrossPay.fileDownload(
    `${FILE_MANAGMENT_END_POINTS.DOWNLOAD}/${id}`,
  );
};
const downloadTemplateFileService = async (): Promise<File | null> => {
  return await HttpCrossPay.fileDownload(
    `${GIFT_CARD_ENDPOINTS.BULK_IMPORT_CLIENTS_TEMPLATE}`,
  );
};
const exportBatchCardsService = async (id: string): Promise<File | null> => {
  return await HttpCrossPay.fileDownload(
    `${GIFT_CARD_ENDPOINTS.EXPORT_BATCH_CARDS}/${id}`,
  );
};
const uploadBulkImportClientsService = async (
  id: string,
  file: File,
): Promise<Response<boolean> | null> => {
  const formData = new FormData();
  formData.append("file", file);
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.BULK_IMPORT_CLIENTS}/${id}/bulk-import`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};
const getRedemptionChannelsService = async (
  queryOptions?: string,
): Promise<Response<PaginatedList<RedemptionChannelModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.REDEMPTION_CHANNELS}${queryOptions ?? ""}`,
  );
};
const getPointOfSaleService = async (
  queryOptions?: string,
): Promise<Response<PaginatedList<PointOfSaleModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.POINT_OF_SALES}${queryOptions ?? ""}`,
  );
};
const getPointOfSaleByIdService = async (
  id: string,
): Promise<Response<PointOfSaleModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.POINT_OF_SALES}/${id}`);
};
const addPointOfSaleService = async (
  pointOfSaleRequest: RequestPointOfSaleModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.POINT_OF_SALES}`,
    pointOfSaleRequest,
  );
};
const updatePointOfSaleService = async (
  pointOfSaleRequest: UpdatePointOfSaleModel,
): Promise<Response<boolean> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.POINT_OF_SALES}`,
    pointOfSaleRequest,
  );
};
const changePointOfSaleAPIKeyService = async (
  id: string,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.CHANGE_POINT_OF_SALES_API_KEY}/${id}`,
  );
};
const getProductsService = async (
  queryOptions?: string,
): Promise<Response<PaginatedList<ProductModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.PRODUCTS}${queryOptions ?? ""}`,
  );
};
const addProductService = async (
  productRequest: ProductRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.PRODUCTS}`,
    productRequest,
  );
};
const editProductService = async (
  productRequest: UpdateProductRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.PRODUCTS}`,
    productRequest,
  );
};
const getProductDetailsByIdService = async (
  id: string,
): Promise<Response<ProductModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.PRODUCTS}/${id}`);
};
const getProductItemsByIdService = async (
  id: string,
  queryOptions?: string,
): Promise<Response<PaginatedList<ProductItemModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GET_PRODUCT_ITEMS}${
      queryOptions ?? ""
    }&productId=${id}`,
  );
};
const getProductItemByBarcodeService = async (
  id: string,
  queryOptions?: string,
): Promise<Response<ProductItemModel> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GET_PRODUCT_ITEM_BY_BARCODE}?barcode=${id}`,
  );
};
const addRedemptionChannelService = async (
  branchRequest: RedemptionChannelRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.REDEMPTION_CHANNELS}`,
    branchRequest,
  );
};
const editRedemptionChannelService = async (
  branchRequest: RedemptionChannelRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.REDEMPTION_CHANNELS}`,
    branchRequest,
  );
};
const getUsersService = async (
  queryOptions?: string,
): Promise<Response<PaginatedList<UserRequestModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.USER}${queryOptions ?? ""}`,
  );
};
const addUserService = async (
  userRequest: UserRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(`${GIFT_CARD_ENDPOINTS.USER}`, userRequest);
};
const getUserDetailsByIdService = async (
  id: string,
): Promise<Response<UserRequestModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.USER}/${id}`);
};
const editUserService = async (
  userRequest: UserRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.put(`${GIFT_CARD_ENDPOINTS.USER}`, userRequest);
};
const getGovernorateLookupService = async (): Promise<Response<
  GovernorateModel[]
> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GET_LOOKUP_GOVERNORATES}`,
  );
};
const getClientLookupService = async (): Promise<Response<
  LookupModel[]
> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.GET_LOOKUP_CLIENTS}`);
};
const getPaymentMethodsLookupService = async (): Promise<Response<
  LookupModel[]
> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GET_LOOKUP_PAYMENT_METHODS}`,
  );
};
const getInvoiceStatusLookupService = async (): Promise<Response<
  LookupModel[]
> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GET_LOOKUP_INVOICE_STATUS}`,
  );
};
const getInvoicesService = async (
  queryOptions?: string,
): Promise<Response<PaginatedList<InvoiceModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.INVOICES}${queryOptions ?? ""}`,
  );
};

const getSalesReportService = async (queryOptions: any) => {
  const response = await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.SALES_REPORT}${queryOptions ?? ""}`,
  );
  return response;
};
const getRedemptionReportService = async (
  queryOptions: any,
): Promise<Response<RedemptionReportResponse> | null> => {
  const response = await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.REDEMPTION_REPORT}${queryOptions ?? ""}`,
  );
  return response;
};
const getProductInventoryReportService = async (
  queryOptions?: any,
): Promise<Response<ProductInventoryReportResponse> | null> => {
  const response = await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.PRODUCT_INVENTORY_REPORT}${queryOptions ?? ""}`,
  );
  return response;
};
const getInvoiceTransactionsService = async (
  id: string,
  queryOptions?: string,
): Promise<Response<PaginatedList<TransactionsData>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.INVOICE_TRANSACTIONS}/${id}${queryOptions ?? ""}`,
  );
};
const getInvoiceDetailsByIdService = async (
  id: string,
): Promise<Response<InvoiceModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.INVOICES}/${id}`);
};
const generateInvoiceService = async (
  id: string,
): Promise<Response<InvoiceModel> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.GENERATE_INVOICE}/${id}`,
  );
};
const getInvoiceProductsService = async (
  id: string,
  queryOptions?: string,
): Promise<Response<PaginatedList<InvoiceProductModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.INVOICES}/${id}/products${queryOptions ?? ""}`,
  );
};
const getProductItemTransactionByBarcodeService = async (
  barcode: string,
  queryOptions?: string,
): Promise<Response<PaginatedList<TransactionsData>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.PRODUCT_ITEM_TRANSACTION}${barcode}${
      queryOptions ?? ""
    }`,
  );
};
const exportTransactionReportDataService = async (
  queryOptions?: string,
): Promise<Response<File> | null> => {
  return await HttpCrossPay.fileDownload(
    `${GIFT_CARD_ENDPOINTS.EXPORT_TRANSACTION_REPORT}${queryOptions ?? ""}`,
  );
};
const exportRedemptionReportDataService = async (
  queryOptions?: string,
): Promise<Response<File> | null> => {
  return await HttpCrossPay.fileDownload(
    `${GIFT_CARD_ENDPOINTS.EXPORT_REDEMPTION_REPORT}${queryOptions ?? ""}`,
  );
};
const exportSalesReportDataService = async (
  queryOptions?: string,
): Promise<Response<File> | null> => {
  return await HttpCrossPay.fileDownload(
    `${GIFT_CARD_ENDPOINTS.EXPORT_SALES_REPORT}${queryOptions ?? ""}`,
  );
};
const exportProductInventoryReportDataService = async (
  queryOptions?: string,
): Promise<Response<File> | null> => {
  return await HttpCrossPay.fileDownload(
    `${GIFT_CARD_ENDPOINTS.EXPORT_PRODUCT_INVENTORY_REPORT}${
      queryOptions ?? ""
    }`,
  );
};
const payInvoiceService = async (
  paymentRequest: InvoicePaymentRequest,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.INVOICE_PAYMENT}`,
    paymentRequest,
  );
};
const getReportConfigService = async (
  reportId: string,
): Promise<Response<PowerBiReportModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.REPORTING}/${reportId}`);
};
const getDashboardStatisticsService =
  async (): Promise<Response<DashboardStatisticsModel> | null> => {
    return await HttpCrossPay.get(
      `${GIFT_CARD_ENDPOINTS.DASHBOARD_STATISTICS}`,
    );
  };
const getBatchesService = async (
  id: string,
  queryOptions?: string,
): Promise<Response<PaginatedList<BatchModel>> | null> => {
  return await HttpCrossPay.get(
    `${GIFT_CARD_ENDPOINTS.BATCHES}/${id}${queryOptions ?? ""}`,
  );
};
const addBatchService = async (
  batchRequest: BatchRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.post(
    `${GIFT_CARD_ENDPOINTS.BATCHES}`,
    batchRequest,
  );
};
const editBatchStatusService = async (
  batchRequest: EditBatchRequestModel,
): Promise<Response<string> | null> => {
  return await HttpCrossPay.put(
    `${GIFT_CARD_ENDPOINTS.UPDATE_BATCH_STATUS}`,
    batchRequest,
  );
};
const getBatchDetailsService = async (
  id: string,
): Promise<Response<BatchModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.BATCH_DETAILS}/${id}`);
};
const giftCardActivateService = async (
  barcode: string,
): Promise<Response<ProductItemModel> | null> => {
  return await HttpCrossPay.post(`${GIFT_CARD_ENDPOINTS.ACTIVATE}/${barcode}`);
};
const giftCardValdiateService = async (
  barcode: string,
): Promise<Response<ProductItemModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.VALIDATE}/${barcode}`);
};
const giftCardDetailsService = async (
  barcode: string,
): Promise<Response<ProductItemModel> | null> => {
  return await HttpCrossPay.get(`${GIFT_CARD_ENDPOINTS.DETAILS}/${barcode}`);
};
const giftCardRedeemService = async (
  barcode: string,
  redeemedBalance: number | null,
): Promise<Response<ProductItemModel> | null> => {
  return await HttpCrossPay.post(`${GIFT_CARD_ENDPOINTS.REDEEM}/${barcode}`, {
    balance: redeemedBalance,
  });
};
export {
  addRedemptionChannelService,
  addClientService,
  addProductService,
  changePassword,
  editRedemptionChannelService,
  editClientService,
  forgetPasswordChangePasswordService,
  getRedemptionChannelByIdService,
  getRedemptionChannelsService,
  getClientDetailsById,
  getFileIdService,
  getProductDetailsByIdService,
  getProductItemsByIdService,
  getProductsService,
  loginService,
  refreshToken,
  requestForgetPasswordOtpService,
  requestVerificationService,
  signupService,
  transactionReportService,
  verifyForgetPasswordOtpService,
  getGovernorateLookupService,
  getClientLookupService,
  getInvoiceStatusLookupService,
  getClientService,
  getPointOfSaleService,
  addPointOfSaleService,
  updatePointOfSaleService,
  getPointOfSaleByIdService,
  changePointOfSaleAPIKeyService,
  getUsersService,
  addUserService,
  getUserDetailsByIdService,
  editUserService,
  downloadFileByIdService,
  deactivateClientService,
  activateClientService,
  getProductItemByBarcodeService,
  editProductService,
  getInvoicesService,
  invoicingReportService,
  getSalesReportService,
  getInvoiceTransactionsService,
  getInvoiceDetailsByIdService,
  getInvoiceProductsService,
  getRedemptionReportService,
  getProductInventoryReportService,
  getProductItemTransactionByBarcodeService,
  exportTransactionReportDataService,
  exportRedemptionReportDataService,
  exportSalesReportDataService,
  exportProductInventoryReportDataService,
  generateInvoiceService,
  getPaymentMethodsLookupService,
  payInvoiceService,
  downloadTemplateFileService,
  uploadBulkImportClientsService,
  getReportConfigService,
  getDashboardStatisticsService,
  getBatchesService,
  addBatchService,
  getBatchDetailsService,
  editBatchStatusService,
  giftCardActivateService,
  giftCardValdiateService,
  giftCardRedeemService,
  giftCardDetailsService,
  exportBatchCardsService,
};
